import React, { useState, useEffect } from 'react';
import { CheckCircle, ChevronDown, MessageCircle, Lock } from 'react-feather';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Input } from 'reactstrap';
import { Button, Loader, EmptyState } from '../../../../../ui';
import { Dialog, Tabs, Callout, Actions, StudentStatusBadge } from '../ui-pending';
import { getCourseLevel, getStatusOfLevel, completedActiveLevelLessons, getLevelLesson,markcompleteLevel, markAsCompleteLesson, getClassId, reGenerateCertificate, updateLessonStatus } from '../../../../../Api/apiUtils';
import './markLessons.scss';
import { toast } from 'react-toastify';
import { MixpanelEvents, trackEvent } from '../../../../../utils/mixpanelTracking';

interface Lesson {
  id: number;
  title: string;
  status: 'completed' | 'in-progress' | 'not-started';
  subtopics?: Subtopic[];

lesson_number:any

}

interface Subtopic {
  id: number;
  title: string;
  status: 'completed' | 'in-progress' | 'not-started';
}

interface CourseLevel {
  id: string;
  name: string;
  lessonRange: string;
  status: 'ongoing' | 'purchased' | 'completed' | 'locked';
  level_id: number;
  start_lesson_number?: number;
  end_lesson_number?: number;
}

const MarkLessons: React.FC<{courseId:any, profileData?:any}> = ({courseId, profileData}) => {
    console.log("selectedCourseData",courseId)
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedLesson, setSelectedLesson] = useState(1);
  const [activeTab, setActiveTab] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState<{[key: string]: boolean}>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [courseLevels, setCourseLevels] = useState<CourseLevel[]>([]);

  const [lessonsData, setLessonsData] = useState<{[key: string]: Lesson[]}>({});
  const [completedLessons, setCompletedLessons] = useState<{[key: string]: any[]}>({});
  const [levelStatuses, setLevelStatuses] = useState<{[key: string]: any}>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedLevelForCompletion, setSelectedLevelForCompletion] = useState<string | null>(null);
  const [isCompletingLevel, setIsCompletingLevel] = useState(false);
  
  // Class selection modal states
  const [showClassSelectionModal, setShowClassSelectionModal] = useState(false);
  const [classList, setClassList] = useState<any[]>([]);
  const [selectedLessonForCompletion, setSelectedLessonForCompletion] = useState<number | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);

  // Uncomplete confirmation modal states
  const [showUncompleteConfirmModal, setShowUncompleteConfirmModal] = useState(false);
  const [selectedLessonForUncompletion, setSelectedLessonForUncompletion] = useState<number | null>(null);
  const [isUncompletingLesson, setIsUncompletingLesson] = useState(false);

  // Clear lesson data when courseId changes
  useEffect(() => {
    if (courseId) {
      // Reset all lesson-related state when course changes
      setLessonsData({});
      setCompletedLessons({});
      setLevelStatuses({});
      setActiveTab('');
      setSelectedLevel('');
      setSelectedLesson(1);
      setShowClassSelectionModal(false);
      setClassList([]);
      setSelectedLessonForCompletion(null);
      setSelectedClassId(null);
    }
  }, [courseId]);

  // Fetch course levels when component mounts or courseId changes
  useEffect(() => {
    const fetchCourseLevels = async () => {
      if (!courseId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await getCourseLevel(courseId);
        console.log('Course levels response:', response);
        
        if (response && response.levels && Array.isArray(response.levels)) {
          // Define the correct sequence order
          const levelSequence = ['Preparatory', 'Intermediate', 'Proficient', 'Advanced'];
          
          const formattedLevels = response.levels
            .filter((level: any) => level.level_name.toLowerCase() !== 'demo') // Filter out Demo level
            .map((level: any) => ({
              id: level.level_id.toString(),
              name: level.level_name,
              lessonRange: level.start_lesson_number && level.end_lesson_number 
                ? `Lesson ${level.start_lesson_number}-${level.end_lesson_number}`
                : 'Lessons',
              status: 'locked' as const,
              level_id: level.level_id,
              start_lesson_number: level.start_lesson_number,
              end_lesson_number: level.end_lesson_number
            }))
            .sort((a: CourseLevel, b: CourseLevel) => {
              // Sort by the predefined sequence
              const aIndex = levelSequence.indexOf(a.name);
              const bIndex = levelSequence.indexOf(b.name);
              
              // If both levels are in the sequence, sort by their position
              if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
              }
              
              // If only one is in the sequence, prioritize the one in sequence
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              
              // If neither is in the sequence, sort alphabetically
              return a.name.localeCompare(b.name);
            });
          
          setCourseLevels(formattedLevels);
          
          // Fetch level statuses first, then set active tab
          await fetchLevelStatuses();
        } else {
          setError('No levels found for this course');
        }
      } catch (err) {
        console.error('Error fetching course levels:', err);
        setError('Failed to fetch course levels');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseLevels();
  }, [courseId, profileData]);

  // Fetch level statuses
  const fetchLevelStatuses = async () => {
    if (!profileData?.userId || !courseId) {
      console.warn('userId and courseId are required to fetch level statuses');
      return;
    }
    
    try {
      const response = await getStatusOfLevel(profileData.userId, courseId);
      console.log('Level statuses response:', response);
      
      if (response && response.status && Array.isArray(response.status)) {
        const statusMap: {[key: string]: any} = {};
        response.status.forEach((status: any) => {
          statusMap[status.level_id.toString()] = status;
        });
        setLevelStatuses(statusMap);
      }
    } catch (err) {
      console.error('Error fetching level statuses:', err);
    }
  };

  // Set active tab to ongoing level when courseLevels and levelStatuses are available
  useEffect(() => {
    if (courseLevels.length > 0 && Object.keys(levelStatuses).length > 0 && !activeTab) {
      // Find the ongoing level
      const ongoingLevel = courseLevels.find(level => {
        const levelStatus = levelStatuses[level.id];
        return levelStatus && levelStatus.status === 1 ;
      });
      
      if (ongoingLevel) {
        // Set ongoing level as active tab
        console.log('Setting ongoing level as active:', ongoingLevel.name);
        setActiveTab(ongoingLevel.id);
        setSelectedLevel(ongoingLevel.id);
      } else {
        // If no ongoing level found, set the first unlocked level or first level
        const firstUnlockedLevel = courseLevels.find(level => {
          const levelStatus = levelStatuses[level.id];
          return levelStatus ;
        });
        
        const levelToActivate = firstUnlockedLevel || courseLevels[0];
        console.log('No ongoing level found, setting first available level:', levelToActivate.name);
        setActiveTab(levelToActivate.id);
        setSelectedLevel(levelToActivate.id);
      }
    }
  }, [courseLevels, levelStatuses]);

  // Fetch completed lessons for a specific level
  const fetchCompletedLessons = async (levelId: string) => {
    // if (!studentUserId) {
    //   console.warn('studentUserId is required to fetch completed lessons');
    //   return;
    // }
  const  studentUserId = profileData.userId
    try {
      const response = await completedActiveLevelLessons(studentUserId, levelId);
      console.log('Completed lessons response:', response);
      
      if (response && response.lessons && Array.isArray(response.lessons)) {
        setCompletedLessons(prev => ({
          ...prev,
          [levelId]: response.lessons
        }));
      }
    } catch (err) {
      console.error('Error fetching completed lessons:', err);
    }
  };

  // Fetch lessons for a specific level
  const fetchLevelLessons = async (levelId: string) => {
    try {
      const response = await getLevelLesson(levelId);
      console.log('Level lessons response:', response);
      
      if (response && response.lessons && Array.isArray(response.lessons)) {
        const formattedLessons = response.lessons.map((lesson: any) => ({
          id: lesson.lesson_id || lesson.id,
          title: lesson.lesson_name || lesson.title || `Lesson ${lesson.lesson_id || lesson.id}`,
          status: lesson.status || 'not-started' as const,
          subtopics: lesson.subtopics || [],
          lesson_number: lesson.lesson_number
        }));
        
        setLessonsData(prev => ({
          ...prev,
          [levelId]: formattedLessons
        }));
      }
    } catch (err) {
      console.error('Error fetching level lessons:', err);
    }
  };

  // Fetch lessons when active tab changes
  useEffect(() => {
    if (activeTab && courseLevels.length > 0) {
      fetchLevelLessons(activeTab);
      fetchCompletedLessons(activeTab);
    }
  }, [activeTab, courseLevels]);

  const currentLessons = lessonsData[activeTab] || [];
  const currentCompletedLessons = completedLessons[activeTab] || [];

  // Check if a lesson is completed
  const isLessonCompleted = (lessonId: number) => {
    return currentCompletedLessons.some((completedLesson: any) => 
      completedLesson.lesson_id === lessonId && completedLesson.completed_at
    );
  };

  // Get lesson status based on completion
  const getLessonStatus = (lessonId: number) => {
    return isLessonCompleted(lessonId) ? 'completed' : 'not-started';
  };

  // Get level status based on API response
  const getLevelStatus = (levelId: string) => {
    const levelStatus = levelStatuses[levelId];
    if (!levelStatus) return 'locked';
    
    if (levelStatus.status === 1 ) {
      return 'ongoing';
    } else if (levelStatus.status === 2) {
      return 'completed';
    } else {
      return 'locked';
    }
  };

  // Check if level should be locked
  const isLevelLocked = (levelId: string) => {
    const levelStatus = levelStatuses[levelId];
    if (!levelStatus) return true;
    
    // If status is 1 (ongoing) or 2 (completed) and purchased, it's unlocked
    if ((levelStatus.status === 1 || levelStatus.status === 2)) {
      return false;
    }
    
    return true;
  };

  // Check if the level above (next level) is locked
  const isNextLevelLocked = (currentLevelId: string) => {
    const currentIndex = courseLevels.findIndex(level => level.id === currentLevelId);
    if (currentIndex === -1 || currentIndex === courseLevels.length - 1) {
      return false; // No next level or current level is the last one
    }
    
    const nextLevel = courseLevels[currentIndex + 1];
    return isLevelLocked(nextLevel.id);
  };

  const toggleDropdown = (tabId: string) => {
    setDropdownOpen(prev => ({
      ...prev,
      [tabId]: !prev[tabId]
    }));
  };

  const updateLevelStatus = (levelId: string, newStatus: 'ongoing' | 'purchased' | 'completed') => {
    setCourseLevels(prev => prev.map(level => 
      level.id === levelId 
        ? { ...level, status: newStatus }
        : level
    ));
  };

  // Mark level as complete
  const markLevelAsComplete = async (levelId: string) => {
    if (!profileData?.userId || !courseId) {
      console.error('Missing required data for marking level as complete');
      return;
    }

    setIsCompletingLevel(true);

    try {
      const levelData = courseLevels.find(level => level.id === levelId);
      if (!levelData) {
        console.error('Level not found');
        setIsCompletingLevel(false);
        return;
      }

      const payload = {
        userId: profileData.userId,
        courseId: parseInt(courseId),
        levelId: parseInt(levelId),
        status: 2,
        isPurchased: true,
        isLessonUpdate:1,
        levelName: levelData.name,
        isMigrated: profileData.isMigrated || false,
        oldAcademics: false,
        startFrom: null
      };

      console.log('Marking level as complete with payload:', payload);
      
      const response = await markcompleteLevel(payload);
      console.log('Level marked as complete:', response);
      
      // Regenerate certificate after completing the level
      try {
        const certificatePayload = {
          userId: profileData.userId,
          userName: profileData.firstName,
          courseId: courseId,
        };
        
        await reGenerateCertificate(certificatePayload);
        console.log('Certificate regenerated successfully');
      } catch (certificateError) {
        console.error('Error regenerating certificate:', certificateError);
        // Don't block the completion flow if certificate generation fails
      }
      
      // Refresh level statuses after completion
      await fetchLevelStatuses();
      
      await fetchCompletedLessons(levelId);
      setShowConfirmModal(false);
      setSelectedLevelForCompletion(null);
      toast.success('Level marked as complete successfully');
      
      // Track Mixpanel event for level completion
      trackEvent(MixpanelEvents.MARK_AS_COMPLETE_LEVEL, {
        page:"Level Marked from lessons tab",
        userId: profileData.userId,
        courseId: courseId,
        levelId: levelId,
        levelName: levelData.name,
        userName: profileData.firstName || 'Unknown',
        completedAt: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error marking level as complete:', error);
      toast.error('Failed to mark level as complete. Please try again.');
    } finally {
      setIsCompletingLevel(false);
    }
  };

  // Handle completion confirmation
  const handleCompleteConfirmation = (levelId: string) => {
    setSelectedLevelForCompletion(levelId);
    setShowConfirmModal(true);
  };

  const markLessonAsComplete = async (lessonId: number) => {
    try {
      setSelectedLessonForCompletion(lessonId);
      
      // Fetch class list for the lesson
      const response = await getClassId(profileData?.userId, courseId);
      console.log('Class list response:', response);
      
      // Check if 204 No Content was returned
      if (response && response.noContent) {
        toast.warning('No classes completed yet');
        return;
      }
      
      if (response && response.length > 0) {
        setClassList(response);
        setShowClassSelectionModal(true);
      } else {
        toast.warning('No classes found for this lesson');
      }
    } catch (error) {
      console.error('Error fetching class list:', error);
      toast.error('Failed to fetch class list. Please try again.');
    }
  };

  const handleClassSelection = (classId: number, classNumber: number) => {
    setSelectedClassId(classId);
  };

  const handleConfirmClassSelection = async () => {
    if (!selectedClassId || !selectedLessonForCompletion) return;
    
    try {
      // Find the selected class item to get classNumber
      const selectedClass = classList.find(item => item.classId === selectedClassId);
      if (!selectedClass) return;

      const completedLesson = currentLessons.find((l) => l.id === selectedLessonForCompletion);

      // Prepare the API payload
      const payload = {
        userId: profileData?.userId,
        lessonId: selectedLessonForCompletion,
        lessonNumber: completedLesson?.lesson_number,
        isTopics: false,
        classId: selectedClassId,
        classNumber: selectedClass.classNumber,
        levelId: activeTab,
        courseId: courseId,
        userPhone: `${profileData?.countryCode ?? ""}${profileData?.mobileNumber ?? ""}`,
      };
      
      console.log('Marking lesson as complete with payload:', payload);
      
      // Call the markAsCompleteLesson API
      const response = await markAsCompleteLesson(payload);
      console.log('Lesson marked as complete:', response);
      
      // Update local state
      setLessonsData(prev => ({
        ...prev,
        [activeTab]: prev[activeTab].map(lesson => 
          lesson.id === selectedLessonForCompletion 
            ? { ...lesson, status: 'completed' }
            : lesson
        )
      }));
      
      // Close modal and reset state
      setShowClassSelectionModal(false);
      setSelectedLessonForCompletion(null);
      setSelectedClassId(null);
      setClassList([]);
      fetchCompletedLessons(activeTab);
      
      // Track Mixpanel event for lesson completion
      trackEvent(MixpanelEvents.MARK_AS_COMPLETE_LESSON, {
        page:"Lesson Marked from lessons tab",
        userId: profileData?.userId,
        courseId: courseId,
        levelId: activeTab,
        lessonId: selectedLessonForCompletion,
        lessonNumber: completedLesson?.lesson_number,
        lessonTitle: completedLesson?.title,
        classId: selectedClassId,
        classNumber: selectedClass.classNumber,
        userName: profileData?.firstName || 'Unknown',
        completedAt: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error marking lesson as complete:', error);
    }
  };

  const handleUncompleteConfirmation = (lessonId: number) => {
    setSelectedLessonForUncompletion(lessonId);
    setShowUncompleteConfirmModal(true);
  };

  const markLessonAsUncomplete = async (lessonId: number) => {
    if (!profileData?.userId) {
      toast.error('User ID is required');
      return;
    }

    setIsUncompletingLesson(true);

    try {
      const payload = {
        userId: profileData.userId,
        lessonId: [lessonId]
      };

      console.log('Marking lesson as uncomplete with payload:', payload);
      
      const response = await updateLessonStatus(payload);
      console.log('Lesson marked as uncomplete:', response);
      
      // Update local state
      setLessonsData(prev => ({
        ...prev,
        [activeTab]: prev[activeTab].map(lesson => 
          lesson.id === lessonId 
            ? { ...lesson, status: 'not-started' }
            : lesson
        )
      }));
      
      // Refresh completed lessons list
      await fetchCompletedLessons(activeTab);
      
      // Close modal and reset state
      setShowUncompleteConfirmModal(false);
      setSelectedLessonForUncompletion(null);
      
      toast.success('Lesson marked as uncomplete successfully');
      
      // Track Mixpanel event for lesson uncompletion
      const uncompletedLesson = currentLessons.find(l => l.id === lessonId);
      trackEvent(MixpanelEvents.MARK_AS_UNCOMPLETE_LESSON, {
        page: "Lesson Marked as Uncomplete from lessons tab",
        userId: profileData?.userId,
        courseId: courseId,
        levelId: activeTab,
        lessonId: lessonId,
        lessonNumber: uncompletedLesson?.lesson_number,
        lessonTitle: uncompletedLesson?.title,
        action: 'uncomplete',
        userName: profileData?.firstName || 'Unknown',
        completedAt: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error marking lesson as uncomplete:', error);
      toast.error('Failed to mark lesson as uncomplete. Please try again.');
    } finally {
      setIsUncompletingLesson(false);
    }
  };

  const isTabLocked = (tabId: string) => {
    const level = courseLevels.find(l => l.id === tabId);
    return level?.status === 'locked';
  };

  const canAccessTab = (tabId: string) => {
    // For now, allow access to all tabs - you can implement your own logic here
    return true;
  };

  const handleTabChange = (tabId: string) => {
    if (!isLevelLocked(tabId)) {
      setActiveTab(tabId);
      setSelectedLevel(tabId);
      setSelectedLesson(1);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="mark-lessons">
        <Loader label="Loading course levels…" />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="mark-lessons">
        <Callout tone="danger" title="Error">
          {error}
        </Callout>
      </div>
    );
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'completed';
      case 'in-progress':
        return 'in-progress';
      case 'ongoing':
        return 'in-progress';
      default:
        return 'not-started';
    }
  };

  return (
    <div className="mark-lessons">
     
        {/* Tab Navigation */}
        {courseLevels.length > 0 && (
        <div className="mark-lessons__tabs">
          <Tabs
            ariaLabel="Course levels"
            value={activeTab}
            onChange={handleTabChange}
            items={courseLevels.map((level) => {
              const isLocked = isLevelLocked(level.id);
              const levelStatus = getLevelStatus(level.id);
              return {
                id: level.id,
                disabled: isLocked,
                label: (
                  <>
                    {level.name}
                    {!isLocked && levelStatus === 'completed' && (
                      <CheckCircle size={14} aria-label="Completed" />
                    )}
                    {isLocked && <Lock size={14} aria-label="Locked" />}
                  </>
                ),
              };
            })}
          />

          {/* Status Display - Show dropdown only for ongoing levels */}
          {courseLevels.map((level) => {
            const isLocked = isLevelLocked(level.id);
            const levelStatus = getLevelStatus(level.id);
            return (
              !isLocked && levelStatus === 'ongoing' && (
                <Dropdown
                  key={level.id}
                  isOpen={dropdownOpen[level.id]}
                  toggle={() => toggleDropdown(level.id)}
                  className="mark-lessons__status-dropdown"
                >
                  <DropdownToggle
                    caret
                    tag="button"
                    type="button"
                    className="ds-btn ds-btn--secondary ds-btn--sm"
                  >
                    {level.name}: Ongoing
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => handleCompleteConfirmation(level.id)}>
                      Mark as Complete
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              )
            );
          })}
        </div>
        )}

        {/* Tab Content */}
        <div className="mark-lessons__content">
          {/* Lesson List */}
          <div className="mark-lessons__lessons">
            {currentLessons.length === 0 ? (
              <EmptyState
                title="No lessons available for this course."
                description="Pick another level, or check that lessons are set up for this course."
              />
            ) : (
              currentLessons.map((lesson) => {
              const isCompleted = isLessonCompleted(lesson.id);
              const lessonStatus = getLessonStatus(lesson.id);
              const nextLevelLocked = isNextLevelLocked(activeTab);
              const currentLevelStatus = getLevelStatus(activeTab);
              
              // Show button if: lesson not completed AND (next level not locked OR current level is ongoing)
              const shouldShowButton = !isCompleted && (!nextLevelLocked || currentLevelStatus === 'ongoing');
              
              return (
              <div
                key={lesson.id}
                className={`mark-lessons__lesson ${
                  selectedLesson === lesson.id ? 'mark-lessons__lesson--selected' : ''
                }`}
                onClick={() => setSelectedLesson(lesson.id)}
              >
                <div className="mark-lessons__lesson-header">
                  <div className="mark-lessons__lesson-info">
                    <span className="ds-strong">Lesson {lesson.lesson_number}</span>
                    <span className="ds-text-muted">{lesson.title}</span>
                  </div>
                  <span className={`mark-lessons__status mark-lessons__status--${getStatusClass(lessonStatus)}`}>
                    {isCompleted && <StudentStatusBadge status="completed" label="✓ Completed" />}
                  </span>
                </div>

                {/* Mark as Complete Button */}
                {shouldShowButton && (
                  <Actions className="mark-lessons__lesson-actions">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<CheckCircle size={14} aria-hidden />}
                      onClick={(e) => {
                        e.stopPropagation();
                        markLessonAsComplete(lesson.id);
                      }}
                    >
                      Mark as Complete
                    </Button>
                  </Actions>
                )}

                {/* Mark as Uncomplete Button */}
                {isCompleted && (
                  <Actions className="mark-lessons__lesson-actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUncompleteConfirmation(lesson.id);
                      }}
                    >
                      Mark as Uncomplete
                    </Button>
                  </Actions>
                )}
              </div>
              );
            })
            )}
          </div>
      </div>

      {/* Chat Icon */}
      <div className="mark-lessons__chat">
        <Button variant="secondary" aria-label="Chat" icon={<MessageCircle size={20} aria-hidden />} />
      </div>

      {/* Confirmation Modal */}
      <Dialog
        isOpen={showConfirmModal}
        onClose={() => !isCompletingLevel && setShowConfirmModal(false)}
        title="Confirm Level Completion"
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowConfirmModal(false)}
              disabled={isCompletingLevel}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => selectedLevelForCompletion && markLevelAsComplete(selectedLevelForCompletion)}
              loading={isCompletingLevel}
            >
              {isCompletingLevel ? 'Processing...' : 'Mark as Complete'}
            </Button>
          </>
        }
      >
        <p>Are you sure you want to mark this level as complete?</p>
        <p>This action cannot be undone.</p>
      </Dialog>

      {/* Class Selection Modal */}
      <Dialog
        isOpen={showClassSelectionModal}
        onClose={() => setShowClassSelectionModal(false)}
        title="Select Class Number"
        subtitle="Please select the class number for this lesson:"
        actions={
          <Button
            variant="primary"
            onClick={handleConfirmClassSelection}
            disabled={!selectedClassId}
          >
            Mark as Complete
          </Button>
        }
      >
        <div className="mark-lessons__class-list" role="radiogroup" aria-label="Class number">
          {classList.map((classItem, index) => (
            <label
              key={index}
              className={`mark-lessons__class-option ${
                selectedClassId === classItem.classId ? 'mark-lessons__class-option--selected' : ''
              }`}
            >
              <Input
                type="radio"
                name="mark-lessons-class"
                checked={selectedClassId === classItem.classId}
                onChange={() => handleClassSelection(classItem.classId, classItem.classNumber)}
              />
              <span className="mark-lessons__class-option-text">
                <span className="ds-strong">Class Number {classItem.classNumber}</span>
                <span className="ds-text-subtle">Class ID: {classItem.classId}</span>
              </span>
            </label>
          ))}
        </div>
      </Dialog>

      {/* Uncomplete Confirmation Modal */}
      <Dialog
        isOpen={showUncompleteConfirmModal}
        onClose={() => !isUncompletingLesson && setShowUncompleteConfirmModal(false)}
        title="Confirm Lesson Uncompletion"
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowUncompleteConfirmModal(false)}
              disabled={isUncompletingLesson}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => selectedLessonForUncompletion && markLessonAsUncomplete(selectedLessonForUncompletion)}
              loading={isUncompletingLesson}
            >
              {isUncompletingLesson ? 'Processing...' : 'Mark as Uncomplete'}
            </Button>
          </>
        }
      >
        <p>Are you sure you want to mark this lesson as uncomplete?</p>
        <p>This action will change the lesson status from completed to Not completed.</p>
      </Dialog>
   
    </div>
  );
};

export default MarkLessons;

